import { Avatar, AvatarFallback } from '@workspace/ui/components/avatar'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import { Separator } from '@workspace/ui/components/separator'
import { Textarea } from '@workspace/ui/components/textarea'
import { ExternalLink, Github, Globe, Linkedin, MapPin, Pencil, Twitter, User } from 'lucide-react'
import { useState } from 'react'
import type { UserProfile, UserProfileUpdate } from './types'

export interface UserProfileViewProps {
  profile: UserProfile
  onSave: (update: UserProfileUpdate) => void
  isSaving?: boolean
  /** Width of the page column; the default is the wide one. */
  widthClassName?: string
}

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email.charAt(0).toUpperCase()
}

function SocialLink({
  icon: Icon,
  url,
  label,
}: {
  icon: typeof Globe
  url: string
  label: string
}) {
  return (
    <a
      href={url.startsWith('http') ? url : `https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <Icon className="size-4" />
      <span className="truncate">{label}</span>
      <ExternalLink className="size-3 shrink-0 opacity-50" />
    </a>
  )
}

export function UserProfileView({
  profile,
  onSave,
  isSaving,
  widthClassName = 'max-w-6xl',
}: UserProfileViewProps) {
  const [editing, setEditing] = useState<'info' | 'links' | null>(null)

  return (
    <div className={`mx-auto ${widthClassName} py-2`}>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Profile</h1>
      <p className="mt-1 text-sm text-muted-foreground">Manage your personal information</p>

      {/* Personal information + bio section */}
      <section className="mt-6">
        <div className="overflow-hidden rounded-xl border border-border bg-card/30">
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-2.5">
              <User className="size-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground">Personal Information</h2>
            </div>
            {editing !== 'info' && (
              <SecondaryButton size="sm" onClick={() => setEditing('info')}>
                <Pencil className="size-3" />
                Edit
              </SecondaryButton>
            )}
          </div>
          <Separator />
          {editing === 'info' ? (
            <EditInfoForm
              profile={profile}
              onSave={(update) => {
                onSave(update)
                setEditing(null)
              }}
              onCancel={() => setEditing(null)}
              isSaving={isSaving}
            />
          ) : (
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="size-16">
                  <AvatarFallback className="bg-muted text-lg font-medium">
                    {getInitials(profile.name, profile.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-base font-medium text-foreground">
                    {profile.name || 'No name set'}
                  </p>
                  <p className="text-sm text-muted-foreground">{profile.email}</p>
                  {profile.location && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="size-3" />
                      {profile.location}
                    </div>
                  )}
                </div>
              </div>
              {profile.bio && <p className="text-sm text-muted-foreground">{profile.bio}</p>}
              {!profile.bio && (
                <p className="text-sm text-muted-foreground/60 italic">No bio added yet.</p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Social links section */}
      <section className="mt-4">
        <div className="overflow-hidden rounded-xl border border-border bg-card/30">
          <div className="flex items-center justify-between px-5 py-4">
            <h2 className="text-sm font-semibold text-foreground">Links</h2>
            {editing !== 'links' && (
              <SecondaryButton size="sm" onClick={() => setEditing('links')}>
                <Pencil className="size-3" />
                Edit
              </SecondaryButton>
            )}
          </div>
          <Separator />
          {editing === 'links' ? (
            <EditLinksForm
              profile={profile}
              onSave={(update) => {
                onSave(update)
                setEditing(null)
              }}
              onCancel={() => setEditing(null)}
              isSaving={isSaving}
            />
          ) : (
            <div className="px-5 py-4 space-y-3">
              {profile.websiteUrl && (
                <SocialLink icon={Globe} url={profile.websiteUrl} label={profile.websiteUrl} />
              )}
              {profile.linkedinUrl && (
                <SocialLink icon={Linkedin} url={profile.linkedinUrl} label={profile.linkedinUrl} />
              )}
              {profile.twitterUrl && (
                <SocialLink icon={Twitter} url={profile.twitterUrl} label={profile.twitterUrl} />
              )}
              {profile.githubUrl && (
                <SocialLink icon={Github} url={profile.githubUrl} label={profile.githubUrl} />
              )}
              {!profile.websiteUrl &&
                !profile.linkedinUrl &&
                !profile.twitterUrl &&
                !profile.githubUrl && (
                  <p className="text-sm text-muted-foreground">No links added yet.</p>
                )}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

// ─── Edit Forms ─────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-xs font-medium text-muted-foreground">{children}</span>
}

function EditActions({
  onSave,
  onCancel,
  isSaving,
}: {
  onSave: () => void
  onCancel: () => void
  isSaving?: boolean
}) {
  return (
    <div className="flex justify-end gap-2 pt-3">
      <SecondaryButton size="sm" onClick={onCancel} disabled={isSaving}>
        Cancel
      </SecondaryButton>
      <CtaButton size="sm" onClick={onSave} loading={isSaving}>
        Save
      </CtaButton>
    </div>
  )
}

function EditInfoForm({
  profile,
  onSave,
  onCancel,
  isSaving,
}: {
  profile: UserProfile
  onSave: (update: UserProfileUpdate) => void
  onCancel: () => void
  isSaving?: boolean
}) {
  const [name, setName] = useState(profile.name ?? '')
  const [location, setLocation] = useState(profile.location ?? '')
  const [bio, setBio] = useState(profile.bio ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const next: Record<string, string> = {}
    if (!name.trim()) next.name = 'Name is required'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  return (
    <form
      className="px-5 py-4 space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (!validate()) return
        onSave({ name: name || null, location: location || null, bio: bio || null })
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <FieldLabel>Full name</FieldLabel>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setErrors((prev) => ({ ...prev, name: '' }))
            }}
            placeholder="Your name"
            className={errors.name ? 'border-red-400' : ''}
          />
          {errors.name && <p className="text-[10px] text-red-400">{errors.name}</p>}
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Email</FieldLabel>
          <Input value={profile.email} disabled className="opacity-60" />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <FieldLabel>Location</FieldLabel>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="City, Country"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Bio</FieldLabel>
        <Textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder="Tell us about yourself..."
          rows={3}
          className="resize-none"
        />
      </div>
      <EditActions
        onSave={() => {
          if (!validate()) return
          onSave({ name: name || null, location: location || null, bio: bio || null })
        }}
        onCancel={onCancel}
        isSaving={isSaving}
      />
    </form>
  )
}

function validateUrl(url: string, prefix: string | string[], label: string): string | null {
  if (!url) return null
  const prefixes = Array.isArray(prefix) ? prefix : [prefix]
  const matched = prefixes.find((p) => url.startsWith(p))
  if (!matched) return `Must start with ${prefixes[0]}`
  if (url.length <= matched.length || url.slice(matched.length) === '/')
    return `${label} username/path is required`
  return null
}

function validateWebsiteUrl(url: string): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') return 'Website must start with https://'
    if (!parsed.hostname.includes('.')) return 'Please enter a valid website URL'
  } catch {
    return 'Please enter a valid website URL (e.g. https://example.com)'
  }
  return null
}

function EditLinksForm({
  profile,
  onSave,
  onCancel,
  isSaving,
}: {
  profile: UserProfile
  onSave: (update: UserProfileUpdate) => void
  onCancel: () => void
  isSaving?: boolean
}) {
  const [websiteUrl, setWebsiteUrl] = useState(profile.websiteUrl ?? '')
  const [linkedinUrl, setLinkedinUrl] = useState(profile.linkedinUrl ?? '')
  const [twitterUrl, setTwitterUrl] = useState(profile.twitterUrl ?? '')
  const [githubUrl, setGithubUrl] = useState(profile.githubUrl ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const clearError = (key: string) => setErrors((e) => ({ ...e, [key]: '' }))

  const validate = () => {
    const next: Record<string, string> = {}
    const websiteErr = validateWebsiteUrl(websiteUrl)
    if (websiteErr) next.websiteUrl = websiteErr
    const linkedinErr = validateUrl(
      linkedinUrl,
      ['https://linkedin.com/in/', 'https://www.linkedin.com/in/'],
      'LinkedIn'
    )
    if (linkedinErr) next.linkedinUrl = linkedinErr
    const twitterErr = validateUrl(twitterUrl, ['https://x.com/', 'https://twitter.com/'], 'X')
    if (twitterErr) next.twitterUrl = twitterErr
    const githubErr = validateUrl(githubUrl, 'https://github.com/', 'GitHub')
    if (githubErr) next.githubUrl = githubErr
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSave = () => {
    if (!validate()) return
    onSave({
      websiteUrl: websiteUrl || null,
      linkedinUrl: linkedinUrl || null,
      twitterUrl: twitterUrl || null,
      githubUrl: githubUrl || null,
    })
  }

  return (
    <form
      className="px-5 py-4 space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        handleSave()
      }}
    >
      <div className="space-y-1.5">
        <FieldLabel>Website</FieldLabel>
        <div className="flex items-center gap-2">
          <Globe className="size-4 shrink-0 text-muted-foreground" />
          <Input
            value={websiteUrl}
            onChange={(e) => {
              setWebsiteUrl(e.target.value)
              clearError('websiteUrl')
            }}
            placeholder="https://example.com"
            className={errors.websiteUrl ? 'border-red-400' : ''}
          />
        </div>
        {errors.websiteUrl && <p className="text-[10px] text-red-400">{errors.websiteUrl}</p>}
      </div>
      <div className="space-y-1.5">
        <FieldLabel>LinkedIn</FieldLabel>
        <div className="flex items-center gap-2">
          <Linkedin className="size-4 shrink-0 text-muted-foreground" />
          <Input
            value={linkedinUrl}
            onChange={(e) => {
              setLinkedinUrl(e.target.value)
              clearError('linkedinUrl')
            }}
            placeholder="https://linkedin.com/in/..."
            className={errors.linkedinUrl ? 'border-red-400' : ''}
          />
        </div>
        {errors.linkedinUrl && <p className="text-[10px] text-red-400">{errors.linkedinUrl}</p>}
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Twitter / X</FieldLabel>
        <div className="flex items-center gap-2">
          <Twitter className="size-4 shrink-0 text-muted-foreground" />
          <Input
            value={twitterUrl}
            onChange={(e) => {
              setTwitterUrl(e.target.value)
              clearError('twitterUrl')
            }}
            placeholder="https://x.com/..."
            className={errors.twitterUrl ? 'border-red-400' : ''}
          />
        </div>
        {errors.twitterUrl && <p className="text-[10px] text-red-400">{errors.twitterUrl}</p>}
      </div>
      <div className="space-y-1.5">
        <FieldLabel>GitHub</FieldLabel>
        <div className="flex items-center gap-2">
          <Github className="size-4 shrink-0 text-muted-foreground" />
          <Input
            value={githubUrl}
            onChange={(e) => {
              setGithubUrl(e.target.value)
              clearError('githubUrl')
            }}
            placeholder="https://github.com/..."
            className={errors.githubUrl ? 'border-red-400' : ''}
          />
        </div>
        {errors.githubUrl && <p className="text-[10px] text-red-400">{errors.githubUrl}</p>}
      </div>
      <EditActions onSave={handleSave} onCancel={onCancel} isSaving={isSaving} />
    </form>
  )
}
